import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Camera,
  FileText,
  Lock,
  ArrowRight,
  ArrowLeft,
  Clock,
  Heart,
  Calendar,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Info,
  Check,
  ChevronRight,
  HelpCircle,
  UserCheck,
  Compass,
  Car,
  AlertTriangle,
  Upload
} from 'lucide-react';
import { registerCompanion } from '../lib/paymentService';
import { sendRegistrationOtpRequest, verifyRegistrationOtpRequest } from '../lib/otpService';
import { DynamicLocationSearchBox } from '../components/location/DynamicLocationSearchBox';
import { DateOfBirthInput } from '../components/common/DateOfBirthInput';
import { validateAdultDob } from '../utils/dobUtils';
import { useAuth } from '../context/AuthContext';
import { MIN_HOURLY_RATE, getDefaultPackageRates } from '../utils/pricingStructure';
import { PairUpLogo } from '../components/common/PairUpLogo';
import { useLanguage } from '../i18n/LanguageContext';

export const CompanionRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const { t } = useLanguage();

  const isSubmittingRef = useRef(false);
  const STORAGE_KEY = 'pairup_companion_registration_draft_v1';

  // Read step from URL search param (?step=1..4)
  const initialUrlStep = parseInt(searchParams.get('step') || '1', 10);
  const safeInitialStep: 1 | 2 | 3 | 4 = (initialUrlStep >= 1 && initialUrlStep <= 4 ? initialUrlStep : 1) as any;
  const [step, setStepState] = useState<1 | 2 | 3 | 4>(safeInitialStep);

  const setStep = (target: 1 | 2 | 3 | 4 | ((prev: 1 | 2 | 3 | 4) => 1 | 2 | 3 | 4), pushHistory = true) => {
    setStepState((prev) => {
      const next = typeof target === 'function' ? target(prev) : target;
      if (pushHistory && next !== prev) {
        setSearchParams((params) => {
          const updated = new URLSearchParams(params);
          updated.set('step', String(next));
          return updated;
        }, { replace: false });
      }
      return next;
    });
  };

  // Sync step whenever URL searchParams change (Browser Back / Forward / Android Hardware Back)
  useEffect(() => {
    const paramVal = parseInt(searchParams.get('step') || '1', 10);
    const validStep: 1 | 2 | 3 | 4 = (paramVal >= 1 && paramVal <= 4 ? paramVal : 1) as any;
    if (validStep !== step) {
      setStepState(validStep);
    }
  }, [searchParams]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [registeredData, setRegisteredData] = useState<any>(null);

  // Registration Mobile OTP Verification State
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [otpVerificationId, setOtpVerificationId] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpMaskedPhone, setOtpMaskedPhone] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [isOtpBusy, setIsOtpBusy] = useState(false);

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = setInterval(() => {
      setOtpCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpCooldown]);

  // Form Fields
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    legalName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    dob: '2000-01-01',
    gender: 'Female',
    city: 'Kolkata, West Bengal',
    hourlyRate: '1500',
    rateOneThirdDay: '2000',
    rateHalfDay: '3000',
    rateFullDay: '5000',
    selectedServices: ['Dinner', 'Tourist Guide'] as string[],
    bio: '',
    idDocumentType: 'Aadhaar Card',
    idDocumentNumber: '',
    idDocumentFrontUrl: '',
    idDocumentBackUrl: '',
    selfieUrl: '',
    // Driver Verification Fields
    drivingLicenceNumber: '',
    drivingLicenceExpiry: '2030-12-31',
    drivingLicenceFrontUrl: 'https://images.unsplash.com/photo-1557683316-973673baf926?auto=format&fit=crop&w=800&q=80',
    drivingLicenceBackUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=800&q=80',
    avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=600&q=80',
    acceptTerms: false,
    acceptSafetyRules: false,
  });

  const handleFileUpload = (field: 'idDocumentFrontUrl' | 'idDocumentBackUrl' | 'selfieUrl', file: File) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type.toLowerCase())) {
      setErrorMessage('Please upload a valid image file (JPG, PNG, or WebP).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage('File size must be under 10MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (dataUrl) {
        setFormData((prev) => ({ ...prev, [field]: dataUrl }));
        setErrorMessage(null);
      }
    };
    reader.onerror = () => {
      setErrorMessage('Failed to read image file. Please try another photo.');
    };
    reader.readAsDataURL(file);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const toggleService = (srvName: string) => {
    setFormData((prev) => {
      const exists = prev.selectedServices.includes(srvName);
      const updated = exists
        ? prev.selectedServices.filter((s) => s !== srvName)
        : [...prev.selectedServices, srvName];
      return { ...prev, selectedServices: updated };
    });
  };

  useEffect(() => {
    if (userProfile && userProfile.email) {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || userProfile.name || '',
        displayName: prev.displayName || userProfile.displayName || '',
        email: prev.email || userProfile.email || '',
        phone: prev.phone || userProfile.phone || '',
      }));
    }
  }, [userProfile]);

  // Hydrate non-sensitive fields from sessionStorage on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          // Explicitly strip passwords and raw document payloads for security & quota preservation
          const {
            password,
            confirmPassword,
            idDocumentFrontUrl,
            idDocumentBackUrl,
            selfieUrl,
            drivingLicenceFrontUrl,
            drivingLicenceBackUrl,
            ...safeFields
          } = parsed;
          setFormData((prev) => ({
            ...prev,
            ...safeFields,
          }));
        }
      }
    } catch {
      // Safe fallback if sessionStorage is restricted or corrupted
    }
  }, []);

  // Save non-sensitive draft fields to sessionStorage whenever formData changes
  useEffect(() => {
    try {
      const {
        password,
        confirmPassword,
        idDocumentFrontUrl,
        idDocumentBackUrl,
        selfieUrl,
        drivingLicenceFrontUrl,
        drivingLicenceBackUrl,
        ...safeFields
      } = formData;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(safeFields));
    } catch {
      // Ignore quota warnings
    }
  }, [formData]);

  // Navigation guard against accidental page close/reload with unsubmitted form data
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isSuccess && !isSubmittingRef.current && (formData.name || formData.phone || formData.email)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formData.name, formData.phone, formData.email, isSuccess]);

  const handleSendStep1Otp = async () => {
    setErrorMessage(null);
    const cleanPhone = formData.phone.replace(/\D/g, '').slice(-10);
    if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      setErrorMessage('Please enter a valid 10-digit Indian mobile number.');
      return;
    }
    setIsOtpBusy(true);
    try {
      const otpRes = await sendRegistrationOtpRequest(cleanPhone);
      if (!otpRes.success || !otpRes.verificationId) {
        setErrorMessage(otpRes.message || 'Could not send verification OTP.');
        return;
      }
      setOtpVerificationId(otpRes.verificationId);
      setOtpMaskedPhone(otpRes.destinationMasked || `+91 ******${cleanPhone.slice(-4)}`);
      setOtpCooldown(otpRes.cooldownSeconds || 60);
      setIsOtpSent(true);
    } finally {
      setIsOtpBusy(false);
    }
  };

  const handleNext = async () => {
    setErrorMessage(null);
    if (step === 1) {
      if (!formData.name || !formData.email || !formData.phone) {
        setErrorMessage('Please fill in your full name, email, and phone number.');
        return;
      }
      const cleanPhone = formData.phone.replace(/\D/g, '').slice(-10);
      if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
        setErrorMessage('Please enter a valid 10-digit Indian mobile number.');
        return;
      }
      if (!formData.password || formData.password !== formData.confirmPassword) {
        setErrorMessage('Passwords do not match or password is missing.');
        return;
      }
      if (formData.gender !== 'Female' && formData.gender !== 'Male') {
        setErrorMessage('Please select your gender (Female or Male).');
        return;
      }
      const dobCheck = validateAdultDob(formData.dob, new Date(), formData.gender);
      if (!dobCheck.valid) {
        setErrorMessage(dobCheck.error || 'Not eligible — your age does not meet the minimum requirement.');
        return;
      }

      if (!isOtpVerified) {
        if (!isOtpSent) {
          await handleSendStep1Otp();
          return;
        }
        const cleanOtp = otpCode.trim().replace(/\D/g, '');
        if (cleanOtp.length !== 6) {
          setErrorMessage('Please enter the 6-digit SMS OTP code sent to your mobile number.');
          return;
        }
        setIsOtpBusy(true);
        try {
          const verifyRes = await verifyRegistrationOtpRequest(cleanPhone, otpVerificationId, cleanOtp);
          if (!verifyRes.success) {
            setErrorMessage(verifyRes.message || 'Invalid or expired OTP code.');
            return;
          }
          setIsOtpVerified(true);
        } finally {
          setIsOtpBusy(false);
        }
      }
    } else if (step === 2) {
      if (!formData.bio || !formData.hourlyRate) {
        setErrorMessage('Please provide a short bio and your hourly service rate.');
        return;
      }
      if (Number(formData.hourlyRate) < MIN_HOURLY_RATE) {
        setErrorMessage(`Minimum hourly rate is ₹${MIN_HOURLY_RATE} per hour according to platform policy.`);
        return;
      }
      if (formData.selectedServices.length === 0) {
        setErrorMessage('Please select at least one service category.');
        return;
      }
    } else if (step === 3) {
      if (!formData.legalName || !formData.idDocumentNumber) {
        setErrorMessage('Please provide your legal name and Government ID document number.');
        return;
      }
      if (!formData.idDocumentFrontUrl) {
        setErrorMessage('Please upload the front side of your Government ID.');
        return;
      }
      if (formData.idDocumentType !== 'PAN Card' && !formData.idDocumentBackUrl) {
        setErrorMessage('Please upload the back side of your Government ID.');
        return;
      }
      if (!formData.selfieUrl) {
        setErrorMessage('Please upload a clear selfie for identity and face verification.');
        return;
      }
      if (formData.selectedServices.includes('Driver')) {
        if (!formData.drivingLicenceNumber.trim()) {
          setErrorMessage('Driving Licence Number is mandatory because you selected Driver service.');
          return;
        }
        if (!formData.drivingLicenceFrontUrl || !formData.drivingLicenceBackUrl) {
          setErrorMessage('Please provide valid front and back images of your Driving Licence.');
          return;
        }
      }
    }
    setStep((prev) => Math.min(prev + 1, 4) as any);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmittingRef.current || isSubmitting) return;

    if (!formData.acceptTerms || !formData.acceptSafetyRules) {
      setErrorMessage('You must accept the Terms of Service and Companion Safety Guidelines.');
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const result = await registerCompanion(formData);
      if (result.success && result.user) {
        setRegisteredData(result.user);
        setIsSuccess(true);
        // Clean up draft storage on successful registration
        try {
          sessionStorage.removeItem(STORAGE_KEY);
        } catch {
          // ignore
        }
        // Automatically save session for companion
        try {
          localStorage.setItem('pairup_user', result.user.email);
          localStorage.setItem('pairup_user_account_type', 'companion');
          localStorage.setItem(
            'pairup_user_profile_data',
            JSON.stringify({
              ...result.user,
              accountType: 'companion',
              companion_profile_status: 'Inactive',
              membership_status: 'PENDING',
              payment_status: 'None',
              payment_verified: false,
              companionProfile: {
                displayName: result.user.displayName,
                hourlyRate: result.user.hourlyRate,
              },
            })
          );
        } catch {
          // ignore
        }
        // Requirement 3 Step 5: Immediate redirection to Membership Selection (Choose Membership Plan & Pay via Razorpay)
        navigate('/membership', {
          replace: true,
          state: {
            fromRegistration: true,
            email: result.user.email,
            isCompanion: true,
          },
        });
      } else {
        setErrorMessage(result.error || 'Failed to complete registration.');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Network error submitting application');
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-10 px-4 flex flex-col justify-center items-center relative overflow-hidden">
      {/* Background glow effects */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-xl w-full space-y-6 relative z-10">
        {/* Top Back Navigation */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              if (step > 1) {
                setStep((step - 1) as any, true);
              } else {
                navigate(-1);
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:text-white text-xs font-semibold transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>{step > 1 ? `Previous Step (Step ${step - 1})` : 'Back'}</span>
          </button>
        </div>

        {/* Top Branding Header */}
        <div className="text-center space-y-2.5">
          <div className="flex justify-center mb-2">
            <PairUpLogo className="w-32 h-32 mx-auto" />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-950/80 border border-purple-800 text-purple-300 text-xs font-bold hover:bg-purple-900 transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5 text-purple-400" />
              <span>PairUpToday Companion Portal</span>
            </Link>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-extrabold shadow-xs">
              💰 Earn Up to ₹1 Lakh
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
            Become a Companion • Earn Up to ₹1 Lakh
          </h1>
          <p className="text-xs text-slate-300 max-w-md mx-auto">
            Join India's most trusted companion platform. Set your own schedule, connect with verified clients, and earn up to ₹1,00,000 per month safely.
          </p>
        </div>

        {/* SRD Mandatory Payment Trigger Rule Notice */}
        <div className="p-4 rounded-2xl bg-gradient-to-r from-purple-950/60 to-slate-900 border border-purple-800/60 text-xs text-slate-300 space-y-1.5">
          <div className="flex items-center gap-2 text-purple-300 font-bold">
            <ShieldCheck className="w-4 h-4 text-purple-400 shrink-0" />
            <span>Mandatory Payment Trigger — Definitive Rule</span>
          </div>
          <p className="text-[11px] text-slate-300 leading-relaxed">
            Registration is <strong>100% Free</strong>. Payment registration-এর সঙ্গে বাধ্যতামূলক নয়। আপনার profile তৈরি, document submission এবং free browsing সম্পন্ন করতে পারবেন। Payment trigger হবে শুধুমাত্র যখন কোনো paid feature বা dual-pairing confirm করবেন।
          </p>
        </div>

        {/* Registration Steps Indicator */}
        {!isSuccess && (
          <div className="flex items-center justify-between px-2">
            {[
              { num: 1, label: 'Account' },
              { num: 2, label: 'Profile' },
              { num: 3, label: 'KYC Info' },
              { num: 4, label: 'Safety & Terms' },
            ].map((s) => (
              <div key={s.num} className="flex flex-col items-center gap-1">
                <div
                  className={`w-8 h-8 rounded-full font-mono text-xs font-bold flex items-center justify-center transition-all ${
                    step === s.num
                      ? 'bg-purple-600 text-white ring-4 ring-purple-600/20'
                      : step > s.num
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-900 border border-slate-800 text-slate-500'
                  }`}
                >
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <span className={`text-[10px] font-bold ${step === s.num ? 'text-purple-300' : 'text-slate-500'}`}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Error Notification */}
        {errorMessage && (
          <div className="p-3.5 rounded-2xl bg-rose-950/80 border border-rose-800/80 text-rose-300 text-xs flex items-center gap-2.5 animate-in fade-in">
            <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Success Screen */}
        {isSuccess ? (
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 text-center space-y-6 shadow-2xl">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto border border-emerald-500/40">
              <CheckCircle2 className="w-10 h-10" />
            </div>

            <div className="space-y-2">
              <span className="px-3 py-1 rounded-full bg-amber-950 border border-amber-800 text-amber-300 text-[11px] font-bold font-mono inline-block">
                STATUS: PENDING VERIFICATION
              </span>
              <h2 className="text-xl sm:text-2xl font-black text-white">
                Application Received Successfully!
              </h2>
              <p className="text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
                Welcome, <strong>{registeredData?.displayName || formData.displayName}</strong>. Your companion profile has been created and is now queued for administrator KYC review.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 text-left text-xs space-y-2 font-mono">
              <div className="flex justify-between text-slate-400">
                <span>Account Role:</span>
                <span className="text-white font-bold">COMPANION</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Public Listing:</span>
                <span className="text-amber-400 font-bold">Pending Admin Approval</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Hourly Rate:</span>
                <span className="text-white font-bold">₹{formData.hourlyRate}/hr</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Initial Payment:</span>
                <span className="text-emerald-400 font-bold">Deferred (₹0 Charged Now)</span>
              </div>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/membership')}
                className="w-full py-3 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 text-white rounded-2xl text-xs font-bold transition-all shadow-lg shadow-pink-950/40 cursor-pointer flex items-center justify-center gap-2"
              >
                <span>{t('companionReg.chooseMembership', 'Step 3: Choose Membership & Activate via Razorpay')}</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => navigate('/companion/dashboard')}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-2xl text-xs font-bold transition-all shadow-lg shadow-purple-950/40 cursor-pointer flex items-center justify-center gap-2"
              >
                <span>{t('companionReg.enterDashboard', 'Enter Companion Dashboard')}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          /* Multi-Step Registration Form */
          <form
            onSubmit={handleSubmit}
            className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-5"
          >
            {/* STEP 1: Basic Account Information */}
            {step === 1 && (
              <div className="space-y-4 animate-in fade-in duration-150">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-white text-sm flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-purple-400" />
                    <span>{t('companionReg.step1Title', 'Step 1: Basic Account Credentials')}</span>
                  </h3>
                  <span className="text-[10px] text-purple-400 font-mono">{t('companionReg.freeSignup', '100% Free Signup')}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">{t('companionReg.govName', 'Government / Legal Name *')}</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          name: val,
                          legalName: prev.legalName || val,
                        }));
                      }}
                      placeholder="e.g. Priya Mukherjee"
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">Profile / Display Name</label>
                    <input
                      type="text"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                      placeholder="e.g. Priya"
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">Email Address *</label>
                    <input
                      type="email"
                      inputMode="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="priya@example.com"
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="font-bold text-slate-300">Mobile Number *</label>
                      {isOtpVerified && (
                        <span className="text-[10px] font-bold text-emerald-400">✓ Verified</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={(e) => {
                          const next = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setFormData((prev) => ({ ...prev, phone: next }));
                          if (isOtpSent || isOtpVerified) {
                            setIsOtpSent(false);
                            setIsOtpVerified(false);
                            setOtpCode('');
                          }
                        }}
                        placeholder="9830012345"
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                      />
                      {!isOtpVerified && (
                        <button
                          type="button"
                          disabled={isOtpBusy || formData.phone.replace(/\D/g, '').length !== 10}
                          onClick={handleSendStep1Otp}
                          className="px-3 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-800 disabled:text-slate-500 text-white font-bold text-xs rounded-xl shrink-0 cursor-pointer"
                        >
                          Verify
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">Password *</label>
                    <input
                      type="password"
                      name="password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">Confirm Password *</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">
                      Gender * <span className="text-[10px] text-slate-400">(Female 18+ / Male 21+)</span>
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="Female">Female (Minimum 18 yrs)</option>
                      <option value="Male">Male (Minimum 21 yrs)</option>
                    </select>
                  </div>

                  <div className="space-y-1 sm:col-span-2">
                    <DateOfBirthInput
                      id="companion-register-dob"
                      value={formData.dob}
                      gender={formData.gender}
                      label={`Date of Birth (${formData.gender === 'Male' ? 'Must be 21+ yrs' : 'Must be 18+ yrs'})`}
                      onChange={(nextDob) =>
                        setFormData((prev) => ({
                          ...prev,
                          dob: nextDob,
                        }))
                      }
                      variant="dark"
                    />
                  </div>
                </div>

                {isOtpSent && !isOtpVerified && (
                  <div className="p-4 rounded-2xl bg-purple-950/60 border border-purple-700/70 space-y-2.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-purple-200">
                        Verify Mobile OTP ({otpMaskedPhone})
                      </span>
                      <button
                        type="button"
                        disabled={otpCooldown > 0 || isOtpBusy}
                        onClick={handleSendStep1Otp}
                        className="text-[11px] font-bold text-purple-300 hover:text-white disabled:text-slate-500 cursor-pointer"
                      >
                        {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : 'Resend OTP'}
                      </button>
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="Enter 6-digit SMS OTP"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full px-3.5 py-2.5 bg-slate-950 border border-purple-600/80 rounded-xl text-white font-mono text-sm font-bold tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                )}

                <div className="pt-2 flex justify-between items-center">
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Home</span>
                  </button>

                  <button
                    type="button"
                    disabled={isOtpBusy}
                    onClick={handleNext}
                    className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-60 text-white rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    <span>
                      {isOtpBusy
                        ? 'Processing OTP...'
                        : !isOtpVerified && !isOtpSent
                        ? 'Send Mobile OTP & Continue'
                        : !isOtpVerified
                        ? 'Verify OTP & Next: Profile Details'
                        : 'Next: Profile Details'}
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: Profile & Service Details */}
            {step === 2 && (
              <div className="space-y-4 animate-in fade-in duration-150">
                <h3 className="font-bold text-white text-sm flex items-center gap-2">
                  <Heart className="w-4 h-4 text-purple-400" />
                  <span>Step 2: Companion Profile & Rates</span>
                </h3>

                <div className="space-y-3 text-xs">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="font-bold text-slate-300 flex items-center justify-between">
                        <span>Primary City Location *</span>
                      </label>
                      <DynamicLocationSearchBox
                        placeholder="Search your city, area, or PIN"
                        selectedLocation={
                          formData.city
                            ? {
                                place_id: `place_${formData.city}`,
                                formatted_address: formData.city,
                                latitude: 22.58,
                                longitude: 88.46,
                                city: formData.city,
                                country: 'India',
                                country_code: 'IN',
                              }
                            : null
                        }
                        onLocationSelect={(loc) => {
                          if (loc) {
                            setFormData((prev) => ({
                              ...prev,
                              city: loc.formatted_address || loc.city || '',
                            }));
                          }
                        }}
                        size="sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="font-bold text-slate-300">Hourly Rate (₹ INR) * (Min ₹600)</label>
                      <input
                        type="number"
                        name="hourlyRate"
                        required
                        min="600"
                        value={formData.hourlyRate}
                        onChange={(e) => {
                          const val = e.target.value;
                          const numeric = Number(val);
                          setFormData((prev) => {
                            const defs = getDefaultPackageRates(numeric || 600);
                            return {
                              ...prev,
                              hourlyRate: val,
                              rateOneThirdDay: prev.rateOneThirdDay || String(defs.rateOneThirdDay),
                              rateHalfDay: prev.rateHalfDay || String(defs.rateHalfDay),
                              rateFullDay: prev.rateFullDay || String(defs.rateFullDay),
                            };
                          });
                        }}
                        placeholder="1500"
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                      />
                    </div>
                  </div>

                  {/* Booking Duration & Package Rates Structure */}
                  <div className="p-3.5 rounded-2xl bg-slate-950/80 border border-slate-800 space-y-2">
                    <label className="font-bold text-slate-200 text-xs block">
                      Custom Duration Package Rates (₹ INR)
                    </label>
                    <p className="text-[11px] text-slate-400">
                      Clients can book Hourly, 1/3 Day (6 hours), Half-Day (12 hours), or Full-Day (24 hours). Set your package rates below:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1">
                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-purple-300">1/3 Day Rate (6 Hours)</label>
                        <input
                          type="number"
                          name="rateOneThirdDay"
                          value={formData.rateOneThirdDay}
                          onChange={handleChange}
                          placeholder="2000"
                          className="w-full px-2.5 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-white font-mono text-xs focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-indigo-300">Half-Day Rate (12 Hours)</label>
                        <input
                          type="number"
                          name="rateHalfDay"
                          value={formData.rateHalfDay}
                          onChange={handleChange}
                          placeholder="3000"
                          className="w-full px-2.5 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-white font-mono text-xs focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[11px] font-bold text-emerald-300">Full-Day Rate (24 Hours)</label>
                        <input
                          type="number"
                          name="rateFullDay"
                          value={formData.rateFullDay}
                          onChange={handleChange}
                          placeholder="5000"
                          className="w-full px-2.5 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-white font-mono text-xs focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Service Categories Selection with Tourist Guide & Driver */}
                  <div className="space-y-2">
                    <label className="font-bold text-slate-300 block">
                      Offered Service Categories (Select all that apply) *
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]">
                      {[
                        { name: 'Tourist Guide', icon: Compass, badge: 'New', color: 'text-emerald-400' },
                        { name: 'Driver', icon: Car, badge: 'Licence Req', color: 'text-blue-400' },
                        { name: 'Dinner', icon: Heart, color: 'text-pink-400' },
                        { name: 'Travel', icon: Compass, color: 'text-purple-400' },
                        { name: 'Movies', icon: Heart, color: 'text-purple-400' },
                        { name: 'Culture', icon: Compass, color: 'text-teal-400' },
                        { name: 'Shopping', icon: Heart, color: 'text-pink-400' },
                        { name: 'Business Events', icon: Heart, color: 'text-indigo-400' },
                        { name: 'Hanging Out', icon: Heart, color: 'text-amber-400' },
                      ].map((item) => {
                        const isSelected = formData.selectedServices.includes(item.name);
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.name}
                            type="button"
                            onClick={() => toggleService(item.name)}
                            className={`p-2.5 rounded-xl border flex items-center justify-between transition-all cursor-pointer text-left ${
                              isSelected
                                ? 'bg-purple-950/80 border-purple-500 text-white shadow-sm'
                                : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-slate-200'
                            }`}
                          >
                            <span className="flex items-center gap-1.5 truncate">
                              <Icon className={`w-3.5 h-3.5 ${item.color} shrink-0`} />
                              <span className="truncate">{item.name}</span>
                            </span>
                            {item.badge && (
                              <span className="text-[9px] bg-slate-800 text-purple-300 px-1.5 py-0.5 rounded-md font-bold shrink-0">
                                {item.badge}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {formData.selectedServices.includes('Driver') && (
                      <div className="p-3 rounded-xl bg-blue-950/60 border border-blue-800/80 text-blue-200 text-xs flex items-start gap-2">
                        <Car className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                        <div>
                          <strong className="text-white block font-bold">Driving Licence Verification Mandatory:</strong>
                          <span>
                            Because you selected <strong>Driver</strong>, uploading a valid Driving Licence (front and back) will be mandatory in Step 3. Your Driver service remains inactive until Admin review and approval.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">Bio & Presentation *</label>
                    <textarea
                      name="bio"
                      rows={3}
                      required
                      value={formData.bio}
                      onChange={handleChange}
                      placeholder="Describe your interests, personality, and experience as a companion, tourist guide, or driver..."
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">Profile Photograph URL</label>
                    <input
                      type="url"
                      name="avatarUrl"
                      value={formData.avatarUrl}
                      onChange={handleChange}
                      placeholder="https://images.unsplash.com/..."
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 text-[11px] font-mono"
                    />
                  </div>
                </div>

                <div className="pt-2 flex justify-between">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition-all cursor-pointer"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    <span>Next: KYC Verification</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: KYC Verification */}
            {step === 3 && (
              <div className="space-y-4 animate-in fade-in duration-150">
                <h3 className="font-bold text-white text-sm flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-purple-400" />
                  <span>Step 3: Identity & KYC Information</span>
                </h3>

                <p className="text-[11px] text-slate-400">
                  To ensure client and companion safety, PairUpToday requires government ID details. Your ID details are stored encrypted and evaluated only by authorized platform administrators.
                </p>

                <div className="space-y-3 text-xs">
                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">Legal Name (As on Government ID) *</label>
                    <input
                      type="text"
                      name="legalName"
                      value={formData.legalName}
                      onChange={handleChange}
                      placeholder="e.g. Priya Mukherjee"
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="font-bold text-slate-300">ID Document Type</label>
                      <select
                        name="idDocumentType"
                        value={formData.idDocumentType}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="Aadhaar Card">Aadhaar Card (Front & Back)</option>
                        <option value="Voter ID">Voter ID (Front & Back)</option>
                        <option value="Driving License">Driving Licence (Front & Back)</option>
                        <option value="Passport">Passport (Required Identity Pages)</option>
                        <option value="PAN Card">PAN Card (Front Side Only)</option>
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="font-bold text-slate-300">ID Number / Reference *</label>
                      <input
                        type="text"
                        name="idDocumentNumber"
                        value={formData.idDocumentNumber}
                        onChange={handleChange}
                        placeholder="e.g. 1234-5678-9012"
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                      />
                    </div>
                  </div>

                  {/* Government ID Document Uploads */}
                  <div className="space-y-3 pt-2">
                    <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="font-bold text-slate-200 text-xs">
                          Upload Government ID Document *
                        </label>
                        <span className="text-[10px] text-purple-400">JPG, PNG, WebP (Max 10MB)</span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {/* Front Side */}
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-semibold text-slate-300 flex items-center justify-between">
                            <span>ID Front Side *</span>
                            {formData.idDocumentFrontUrl && (
                              <span className="text-emerald-400 text-[10px]">✓ Selected</span>
                            )}
                          </label>
                          <label className="flex flex-col items-center justify-center p-3 border-2 border-dashed border-slate-700 hover:border-purple-500 rounded-xl cursor-pointer bg-slate-900/60 hover:bg-purple-950/20 transition-all text-center">
                            <Upload className="w-5 h-5 text-purple-400 mb-1" />
                            <span className="text-[11px] text-slate-300 font-medium">Click to choose Front file</span>
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleFileUpload('idDocumentFrontUrl', file);
                              }}
                            />
                          </label>
                          {formData.idDocumentFrontUrl && (
                            <div className="h-20 rounded-lg overflow-hidden border border-slate-700 relative">
                              <img src={formData.idDocumentFrontUrl} alt="Front ID Preview" className="w-full h-full object-cover" />
                            </div>
                          )}
                        </div>

                        {/* Back Side (optional only for PAN) */}
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-semibold text-slate-300 flex items-center justify-between">
                            <span>ID Back Side {formData.idDocumentType === 'PAN Card' ? '(Optional)' : '*'}</span>
                            {formData.idDocumentBackUrl && (
                              <span className="text-emerald-400 text-[10px]">✓ Selected</span>
                            )}
                          </label>
                          <label className="flex flex-col items-center justify-center p-3 border-2 border-dashed border-slate-700 hover:border-purple-500 rounded-xl cursor-pointer bg-slate-900/60 hover:bg-purple-950/20 transition-all text-center">
                            <Upload className="w-5 h-5 text-purple-400 mb-1" />
                            <span className="text-[11px] text-slate-300 font-medium">Click to choose Back file</span>
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleFileUpload('idDocumentBackUrl', file);
                              }}
                            />
                          </label>
                          {formData.idDocumentBackUrl && (
                            <div className="h-20 rounded-lg overflow-hidden border border-slate-700 relative">
                              <img src={formData.idDocumentBackUrl} alt="Back ID Preview" className="w-full h-full object-cover" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Live Selfie Verification */}
                      <div className="space-y-1.5 pt-2 border-t border-slate-800">
                        <label className="text-[11px] font-semibold text-slate-300 flex items-center justify-between">
                          <span className="flex items-center gap-1.5">
                            <Camera className="w-3.5 h-3.5 text-pink-400" />
                            <span>Live Selfie / Face Verification Photo *</span>
                          </span>
                          {formData.selfieUrl && (
                            <span className="text-emerald-400 text-[10px]">✓ Selected</span>
                          )}
                        </label>
                        <div className="flex items-center gap-3">
                          <label className="flex-1 flex flex-col items-center justify-center p-3 border-2 border-dashed border-slate-700 hover:border-pink-500 rounded-xl cursor-pointer bg-slate-900/60 hover:bg-pink-950/20 transition-all text-center">
                            <Camera className="w-5 h-5 text-pink-400 mb-1" />
                            <span className="text-[11px] text-slate-300 font-medium">Take or upload live selfie</span>
                            <span className="text-[10px] text-slate-500">Must be clear and well-lit</span>
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              capture="user"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleFileUpload('selfieUrl', file);
                              }}
                            />
                          </label>
                          {formData.selfieUrl && (
                            <div className="w-20 h-20 rounded-xl overflow-hidden border border-slate-700 shrink-0 relative">
                              <img src={formData.selfieUrl} alt="Selfie Preview" className="w-full h-full object-cover" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* MANDATORY DRIVING LICENCE UPLOAD IF DRIVER SERVICE SELECTED */}
                  {formData.selectedServices.includes('Driver') && (
                    <div className="p-4 rounded-2xl bg-blue-950/40 border-2 border-blue-700/60 space-y-3 mt-3">
                      <div className="flex items-center gap-2 text-blue-300 font-bold">
                        <Car className="w-4 h-4 text-blue-400" />
                        <span>Mandatory Driving Licence Verification (Driver Category)</span>
                      </div>
                      <p className="text-[11px] text-blue-200/80 leading-relaxed">
                        To offer Driver services on PairUpToday, you must upload a valid Driving Licence. Your Driver status remains inactive until administrator verification and approval.
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold text-slate-300">Driving Licence Number *</label>
                          <input
                            type="text"
                            name="drivingLicenceNumber"
                            required
                            value={formData.drivingLicenceNumber}
                            onChange={handleChange}
                            placeholder="e.g. DL-0420110012345"
                            className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white font-mono text-xs focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="font-bold text-slate-300">Licence Expiry Date *</label>
                          <input
                            type="date"
                            name="drivingLicenceExpiry"
                            required
                            value={formData.drivingLicenceExpiry}
                            onChange={handleChange}
                            className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white font-mono text-xs focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                        <div className="space-y-1">
                          <label className="font-bold text-slate-300">Licence Front Photo URL *</label>
                          <input
                            type="url"
                            name="drivingLicenceFrontUrl"
                            required
                            value={formData.drivingLicenceFrontUrl}
                            onChange={handleChange}
                            placeholder="https://..."
                            className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white font-mono text-[11px] focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="font-bold text-slate-300">Licence Back Photo URL *</label>
                          <input
                            type="url"
                            name="drivingLicenceBackUrl"
                            required
                            value={formData.drivingLicenceBackUrl}
                            onChange={handleChange}
                            placeholder="https://..."
                            className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white font-mono text-[11px] focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-2 flex justify-between">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition-all cursor-pointer"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    <span>Next: Terms & Submit</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: Safety & Terms Acceptance */}
            {step === 4 && (
              <div className="space-y-4 animate-in fade-in duration-150">
                <h3 className="font-bold text-white text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-400" />
                  <span>{t('companionReg.step4Title', 'Step 4: Safety & Companion Agreement')}</span>
                </h3>

                <div className="space-y-3 p-4 bg-slate-950 rounded-2xl border border-slate-800 text-xs text-slate-300">
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleChange}
                      className="mt-0.5 w-4 h-4 rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 cursor-pointer"
                    />
                    <span>
                      {t('companionReg.acceptTerms', 'I accept the PairUpToday Terms of Service & Privacy Policy')}
                    </span>
                  </label>

                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      name="acceptSafetyRules"
                      checked={formData.acceptSafetyRules}
                      onChange={handleChange}
                      className="mt-0.5 w-4 h-4 rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 cursor-pointer"
                    />
                    <span>
                      {t('companionReg.acceptSafety', 'I commit to safe public-only meetups & safety guidelines')}
                    </span>
                  </label>
                </div>

                <div className="p-3.5 rounded-2xl bg-purple-950/40 border border-purple-800/60 text-[11px] text-purple-300 flex items-center gap-2">
                  <Info className="w-4 h-4 shrink-0 text-purple-400" />
                  <span>
                    Your profile will initially be marked <strong>PENDING_VERIFICATION</strong>. Once reviewed and approved by Admin, it will be listed in public discovery.
                  </span>
                </div>

                <div className="pt-2 flex justify-between items-center">
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition-all cursor-pointer"
                  >
                    {t('companionReg.backBtn', 'Back')}
                  </button>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shadow-lg shadow-purple-950/40 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Submitting Application...</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        <span>{t('companionReg.submitBtn', 'Submit Registration')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
        )}

        {/* Bottom Login Link */}
        <div className="text-center text-xs text-slate-400">
          Already registered as a companion?{' '}
          <Link to="/login" className="text-purple-400 hover:text-purple-300 font-bold underline">
            Sign in to Companion Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};
